import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  BookOpenIcon,
  CheckCircle2Icon,
  ChevronRightIcon,
  CircleIcon,
  GraduationCapIcon,
  LightbulbIcon,
  TrophyIcon,
  XCircleIcon,
} from 'lucide-react';
import { apiGet } from '../../api/client.js';
import { ELEARNING_BASE } from '../../pages/ELearnings.js';
import { CertificaatKnop } from './CertificaatKnop.js';
import type { CourseDetailResponse } from '../../pages/ELearnings.js';
import { AudienceChip, ProgressBar } from './StudentCourseOverview.js';

export function StudentCourseDetail({
  courseId,
  onBack,
  onOpenLesson,
  onOpenPractice,
  onStartExam,
}: {
  courseId: string;
  onBack: () => void;
  onOpenLesson: (lessonId: string) => void;
  onOpenPractice: (moduleId: string) => void;
  onStartExam: () => void;
}): JSX.Element {
  const [data, setData] = useState<CourseDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    void apiGet<CourseDetailResponse>(`${ELEARNING_BASE}/courses/${courseId}`)
      .then(setData)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [courseId]);

  const completedSet = useMemo(
    () => new Set(data?.progress.completedLessonIds ?? []),
    [data?.progress.completedLessonIds],
  );

  const sortedModules = useMemo(
    () => [...(data?.modules ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [data?.modules],
  );

  if (loading) {
    return <div className="py-16 text-center text-[13px] text-ink-900/40">Cursus laden…</div>;
  }

  if (loadError || !data) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-8">
        <div className="rounded-2xl border border-dashed border-brand-200 py-16 text-center">
          <BookOpenIcon size={28} className="mx-auto mb-3 text-ink-900/20" />
          <p className="text-[14px] font-medium text-error">De cursus kon niet worden geladen.</p>
          <button
            type="button"
            onClick={onBack}
            className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-700 hover:underline"
          >
            <ArrowLeftIcon size={14} />
            Terug naar overzicht
          </button>
        </div>
      </div>
    );
  }

  const { course, progress } = data;

  return (
    <div className="mx-auto max-w-3xl px-5 py-8">
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-700 hover:underline"
      >
        <ArrowLeftIcon size={14} />
        Academy
      </button>

      {/* Kop */}
      <div className="mb-6">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <AudienceChip audience={course.audience} />
        </div>
        <h1 className="text-[24px] font-bold leading-tight text-ink-900">{course.name}</h1>
        {course.description && (
          <p className="mt-2 text-[14px] leading-relaxed text-ink-900/65">{course.description}</p>
        )}
      </div>

      {/* Voortgangsbalk */}
      <div className="mb-8 rounded-2xl border border-brand-100 bg-white p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between text-[13px]">
          <span className="text-ink-900/55">Voortgang</span>
          <span className="font-medium text-ink-900">
            {progress.videoQuestionsTotal > 0
              ? `${progress.videoQuestionsAnswered} / ${progress.videoQuestionsTotal} vragen beantwoord`
              : `${progress.completedLessons} / ${progress.totalLessons} lessen`}
          </span>
        </div>
        <ProgressBar percentage={progress.progressPercentage} />
        <p className="mt-1.5 text-right text-[11px] text-ink-900/45">
          {progress.progressPercentage}% voltooid
        </p>
      </div>

      {/* Modules met lessen */}
      <div className="mb-8 space-y-4">
        {sortedModules.map((module) => {
          const lessons = [...module.lessons].sort((a, b) => a.sortOrder - b.sortOrder);
          const completedInModule = lessons.filter((l) => completedSet.has(l.id)).length;
          const vragen = lessons.some((l) => l.hasVideo) ? module.videoQuestions : [];
          const vragenBeantwoord = vragen.filter((q) => q.id in progress.videoAnswers).length;

          return (
            <div
              key={module.id}
              className="overflow-hidden rounded-2xl border border-brand-100 bg-white shadow-sm"
            >
              <div className="flex items-center justify-between border-b border-brand-100 px-4 py-3">
                <h2 className="flex items-center gap-2 text-[15px] font-bold text-ink-900">
                  <BookOpenIcon size={17} className="text-brand-600" />
                  {module.name}
                </h2>
                <span className="text-[11px] text-ink-900/50">
                  {vragen.length > 0
                    ? `${vragenBeantwoord} / ${vragen.length} vragen`
                    : `${completedInModule} / ${lessons.length} lessen`}
                </span>
              </div>

              <ul className="p-2">
                {lessons.map((lesson) => {
                  const isCompleted = completedSet.has(lesson.id);
                  return (
                    <li key={lesson.id}>
                      <button
                        type="button"
                        onClick={() => onOpenLesson(lesson.id)}
                        className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-brand-50"
                      >
                        {isCompleted ? (
                          <CheckCircle2Icon size={18} className="shrink-0 text-green-600" />
                        ) : (
                          <CircleIcon size={18} className="shrink-0 text-ink-900/25" />
                        )}
                        <span
                          className={`flex-1 text-[13px] ${
                            isCompleted ? 'text-ink-900/55' : 'text-ink-900'
                          }`}
                        >
                          {lesson.name}
                        </span>
                        <ChevronRightIcon
                          size={15}
                          className="shrink-0 text-ink-900/20 opacity-0 transition-opacity group-hover:opacity-100"
                        />
                      </button>
                    </li>
                  );
                })}
                {lessons.length === 0 && (
                  <li className="px-3 py-2.5 text-[12px] text-ink-900/40">
                    Nog geen lessen in dit onderdeel.
                  </li>
                )}
              </ul>

              {module.practiceCount > 0 && (
                <div className="border-t border-brand-100 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => onOpenPractice(module.id)}
                    className="inline-flex items-center gap-2 rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-[12px] font-medium text-brand-700 transition-colors hover:bg-brand-50"
                  >
                    <LightbulbIcon size={14} />
                    Oefenen ({module.practiceCount}{' '}
                    {module.practiceCount === 1 ? 'vraag' : 'vragen'})
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Cursus zonder examen: alle lessen af = afgerond, certificaat zonder score. */}
      {!progress.hasExam && progress.status === 'geslaagd' && (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-6 text-center">
          <TrophyIcon size={40} className="mx-auto mb-3 text-green-600" />
          <h2 className="text-[18px] font-bold text-ink-900">
            Gefeliciteerd, je hebt de training afgerond.
          </h2>
          {course.certificateEnabled && <CertificaatKnop courseId={courseId} />}
        </div>
      )}

      {/* Examensectie */}
      {progress.hasExam && (
        <ExamSection
          status={progress.status}
          canStartExam={progress.canStartExam}
          bestExamScore={progress.bestExamScore}
          attemptsRemaining={progress.attemptsRemaining}
          onStartExam={onStartExam}
          courseId={courseId}
          certificateEnabled={course.certificateEnabled}
        />
      )}
    </div>
  );
}

function ExamSection({
  status,
  canStartExam,
  bestExamScore,
  attemptsRemaining,
  onStartExam,
  courseId,
  certificateEnabled,
}: {
  status: CourseDetailResponse['progress']['status'];
  canStartExam: boolean;
  bestExamScore: number | null;
  attemptsRemaining: number;
  onStartExam: () => void;
  courseId: string;
  certificateEnabled: boolean;
}): JSX.Element {
  if (status === 'geslaagd') {
    return (
      <div className="rounded-2xl border border-green-200 bg-green-50 p-6 text-center">
        <TrophyIcon size={40} className="mx-auto mb-3 text-green-600" />
        <h2 className="text-[18px] font-bold text-ink-900">Gefeliciteerd, je bent geslaagd.</h2>
        {bestExamScore != null && (
          <p className="mt-1 text-[14px] text-ink-900/65">
            Je score: <span className="font-semibold text-green-700">{bestExamScore}%</span>
          </p>
        )}
        {certificateEnabled && <CertificaatKnop courseId={courseId} />}
      </div>
    );
  }

  // Gezakt zonder pogingen meer: einde. Anders (gezakt met pogingen of
  // examenklaar) mag de gebruiker (opnieuw) starten. De cooldown/limiet
  // wordt bij het daadwerkelijk starten door de backend afgedwongen.
  if (status === 'gezakt' && attemptsRemaining <= 0) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
        <XCircleIcon size={40} className="mx-auto mb-3 text-red-600" />
        <h2 className="text-[18px] font-bold text-ink-900">Maximaal aantal pogingen bereikt</h2>
        <p className="mt-1 text-[14px] text-ink-900/65">
          Neem contact op met SUPPOHRT voor een herkansing.
        </p>
      </div>
    );
  }

  if (status === 'examenklaar' || status === 'gezakt') {
    return (
      <div className="rounded-2xl border border-brand-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
          <div className="text-center sm:text-left">
            <h2 className="text-[16px] font-bold text-ink-900">
              {status === 'gezakt' ? 'Klaar voor een herkansing' : 'Klaar voor het examen'}
            </h2>
            <p className="mt-1 text-[13px] text-ink-900/65">
              Je hebt alle lessen afgerond. Je kunt het examen nu starten.
            </p>
            {status === 'gezakt' && (
              <p className="mt-1 text-[12px] text-ink-900/50">
                Nog {attemptsRemaining} poging{attemptsRemaining !== 1 ? 'en' : ''} over.
              </p>
            )}
          </div>
          <button
            type="button"
            disabled={!canStartExam}
            onClick={onStartExam}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <GraduationCapIcon size={16} />
            {status === 'gezakt' ? 'Herkansing starten' : 'Start examen'}
          </button>
        </div>
        {!canStartExam && (
          <p className="mt-3 text-[12px] text-ink-900/50">
            Het examen is nu nog niet beschikbaar (bijvoorbeeld door een wachttijd na een vorige
            poging).
          </p>
        )}
      </div>
    );
  }

  // status === 'bezig' — nog niet alle lessen af.
  return (
    <div className="rounded-2xl border border-dashed border-brand-200 bg-brand-50/40 p-6 text-center">
      <GraduationCapIcon size={32} className="mx-auto mb-2 text-ink-900/30" />
      <p className="text-[13px] font-medium text-ink-900/55">
        Rond eerst alle lessen af om het examen te kunnen starten.
      </p>
    </div>
  );
}
